import {eq, eqStrict} from 'fp-ts/lib/Eq';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import * as A from 'fp-ts/lib/ReadonlyArray';
import {createSelector} from 'reselect';

import {ExponentialBackoffOptions} from '../../../helpers/controlHelpers';
import {VisualCustomView} from '../../../helpers/models/customViewHelpers';
import {defaultIsCustomViewExpanded, dummyFindIsExpanded} from '../../../helpers/visualCustomViewHelpers';
import {CustomViewTypesEnum} from '../../../models/api/customViewModel';
import {InboxesFiltersEnum} from '../../../models/url/inboxesUrlModel';
import {AppState} from '../../../store/appState';
import {getSortOptions} from '../../../store/data/localPreferences/localPreferencesSelectors';
import {getShouldAlphabetizeWorkspace} from '../../../store/data/preferences/preferencesSelectors';
import {
  makeGetUserVisualWorkspaceTeammateWorkspaceTags,
  makeGetVisualWorkspaceTeammateWorkspaceVisualCustomViews,
} from '../../../store/data/workspace/visualWorkspace/visualWorkspaceSelectors';
import {
  InboxesParametersTypesEnum,
  InboxesTeammateViewParameters,
} from '../../../store/panels/inboxes/parameters/inboxesParameters';
import {emptyInboxesSelection} from '../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {buildParametersFromTemplate} from '../../../store/panels/inboxes/parameters/inboxesParametersSelectors';
import {Prefetcher} from './base';
import {computeInitialCellsCondition, prefetchInitialCellsAsync} from './initialCells';

/*
 * Factory.
 */

/** Creates a prefetcher that prefetches the first page of P2 of all pinned private tags. */
export function createPrivateTagViewsInitialCellsPrefetcher(
  filter: InboxesFiltersEnum,
  exponentialBackoffOptions: ExponentialBackoffOptions,
): Prefetcher<InboxesTeammateViewParameters> {
  const computeCondition = (state: AppState, view: VisualCustomView) => {
    const parameters = buildTeammateViewCellsParameters(state, view.id, filter);

    return pipe(
      computeInitialCellsCondition(state, parameters),
      O.map(() => parameters),
    );
  };

  return {
    key: `initial-user-views-cells-${filter}`,
    computeBacklogLength: (state) =>
      pipe(
        getPrivateTagVisualCustomViews(state),
        A.filterMap((view) => computeCondition(state, view)),
        (views) => views.length,
      ),
    computeCondition: (state) =>
      pipe(
        getPrivateTagVisualCustomViews(state),
        A.findFirstMap((view) => computeCondition(state, view)),
      ),
    C: eqTeammateViewCellsParameters,
    prefetchAsync: (dispatch, parameters) =>
      prefetchInitialCellsAsync(dispatch, parameters, exponentialBackoffOptions),
  };
}

/*
 * Helpers.
 */

const getPersonalWorkspaceVisualCustomViews = makeGetVisualWorkspaceTeammateWorkspaceVisualCustomViews<void>({
  getVisualWorkspaceTags: makeGetUserVisualWorkspaceTeammateWorkspaceTags(getShouldAlphabetizeWorkspace),
  getOptionalAdditionalTagsMap: () => undefined,
  getOptionalParentTagOverridesMap: () => undefined,
  getFindIsItemExpanded: () => dummyFindIsExpanded,
  defaultExpandedState: defaultIsCustomViewExpanded,
  shouldRestrictToVisibleItems: true,
});

const getPrivateTagVisualCustomViews = createSelector(getPersonalWorkspaceVisualCustomViews, (views) =>
  views.filter((view) => view.type === CustomViewTypesEnum.PRIVATE_TAG),
);

const eqTeammateViewCellsParameters = eq.contramap(
  eqStrict,
  (parameters: InboxesTeammateViewParameters) => parameters.viewId,
);

function buildTeammateViewCellsParameters(
  state: AppState,
  teammateViewId: number,
  filter: InboxesFiltersEnum,
): InboxesTeammateViewParameters {
  const sortOptions = getSortOptions(state);
  return buildParametersFromTemplate(sortOptions, {
    type: InboxesParametersTypesEnum.TEAMMATE_VIEW,
    viewId: teammateViewId,
    filters: [filter],
    selection: emptyInboxesSelection,
  });
}
